import { capitalCase } from '../text/text.js';
const isObject = (value) => typeof value === 'object' && value !== null;
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
const getString = (value) => isNonEmptyString(value) ? value.trim() : undefined;
const getNested = (obj, path) => {
    let current = obj;
    for (const key of path) {
        if (!isObject(current))
            return undefined;
        current = current[key];
    }
    return current;
};
const isAxiosLikeError = (err) => {
    if (!isObject(err))
        return false;
    return (err.isAxiosError === true ||
        'response' in err ||
        'request' in err ||
        err.name === 'AxiosError');
};
const cleanStackLine = (line) => line
    .replace(/^at\s+/, '')
    .replace(/\?.*?(?=:\d+:\d+|$)/g, '')
    .trim();
const isNoiseFrame = (line) => {
    const normalized = line.toLowerCase();
    return [
        'node_modules',
        'react-dom',
        'scheduler',
        '__vite',
        '/vite/',
        'webpack',
        'bundle.js',
        'chunk-',
        '<anonymous>',
    ].some((noise) => normalized.includes(noise));
};
const normalizeFunctionName = (rawFunctionName) => {
    let functionName = rawFunctionName.trim();
    if (!functionName)
        return undefined;
    if (functionName.startsWith('Object.')) {
        functionName = functionName.slice('Object.'.length);
    }
    functionName = functionName
        .replace(/^Module\./, '')
        .replace(/^__webpack_exports__\./, '')
        .replace(/\.<anonymous>$/, '')
        .trim();
    if (!functionName ||
        functionName === 'Object' ||
        functionName === '<anonymous>' ||
        functionName === 'anonymous') {
        return undefined;
    }
    return functionName;
};
const getFileNameFromPath = (fullPath) => {
    const normalized = fullPath.replace(/\\/g, '/');
    const parts = normalized.split('/');
    return parts[parts.length - 1] || fullPath;
};
const parseStackLine = (line) => {
    const cleaned = cleanStackLine(line);
    const withFunctionMatch = cleaned.match(/^(.*?) \((.+):(\d+):(\d+)\)$/);
    if (withFunctionMatch) {
        const [, rawFunctionName, fullPath, lineNumber, columnNumber] = withFunctionMatch;
        return {
            raw: cleaned,
            functionName: normalizeFunctionName(rawFunctionName),
            fileName: getFileNameFromPath(fullPath),
            line: lineNumber,
            column: columnNumber,
        };
    }
    const noFunctionMatch = cleaned.match(/^(.+):(\d+):(\d+)$/);
    if (noFunctionMatch) {
        const [, fullPath, lineNumber, columnNumber] = noFunctionMatch;
        return {
            raw: cleaned,
            fileName: getFileNameFromPath(fullPath),
            line: lineNumber,
            column: columnNumber,
        };
    }
    return { raw: cleaned };
};
const getCurrentRoute = () => {
    if (typeof window === 'undefined')
        return undefined;
    const pathname = getString(window.location?.pathname);
    const search = getString(window.location?.search);
    const hash = getString(window.location?.hash);
    const route = `${pathname ?? ''}${search ?? ''}${hash ?? ''}`.trim();
    return route || undefined;
};
const formatFileLine = (frame) => {
    if (isNonEmptyString(frame.fileName) && isNonEmptyString(frame.line)) {
        return `${frame.fileName}:${frame.line}`;
    }
    if (isNonEmptyString(frame.fileName)) {
        return frame.fileName;
    }
    return undefined;
};
const isLikelyReactComponentName = (name) => !!name && /^[A-Z][A-Za-z0-9_$]*$/.test(name);
const isLikelyHookName = (name) => !!name && /^use[A-Z0-9_]/.test(name);
const isLikelyJsxFile = (fileName) => !!fileName && /\.(tsx|jsx)$/.test(fileName);
const getBestComponentFromFrames = (frames) => {
    for (const frame of frames) {
        if (isLikelyReactComponentName(frame.functionName)) {
            return frame.functionName;
        }
    }
    for (const frame of frames) {
        if (isLikelyJsxFile(frame.fileName) &&
            isLikelyReactComponentName(frame.functionName)) {
            return frame.functionName;
        }
    }
    for (const frame of frames) {
        if (isLikelyJsxFile(frame.fileName) &&
            isLikelyHookName(frame.functionName)) {
            const baseFileName = frame.fileName?.replace(/\.(tsx|jsx)$/, '');
            if (baseFileName && /^[A-Z]/.test(baseFileName)) {
                return baseFileName;
            }
        }
    }
    for (const frame of frames) {
        if (isLikelyJsxFile(frame.fileName)) {
            const baseFileName = frame.fileName?.replace(/\.(tsx|jsx)$/, '');
            if (baseFileName && /^[A-Z]/.test(baseFileName)) {
                return baseFileName;
            }
        }
    }
    return undefined;
};
const getBestLocationFromFrames = (frames) => {
    for (const frame of frames) {
        if (frame.functionName) {
            const fileLine = formatFileLine(frame);
            if (fileLine)
                return `${frame.functionName} (${fileLine})`;
            return frame.functionName;
        }
    }
    for (const frame of frames) {
        const fileLine = formatFileLine(frame);
        if (fileLine)
            return fileLine;
    }
    return 'unknown location';
};
const getStackFrames = (err) => {
    const stack = (err instanceof Error ? err.stack : undefined) ??
        getString(getNested(err, ['stack'])) ??
        getString(getNested(err, ['response', 'data', 'stack']));
    if (!stack)
        return [];
    return stack
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .filter((line) => line.startsWith('at '))
        .map(parseStackLine)
        .filter((frame) => !isNoiseFrame(frame.raw));
};
export const getType = (err) => {
    if (isAxiosLikeError(err)) {
        const status = err.response?.status;
        if (status)
            return `HTTP ${status}`;
        return 'Request';
    }
    if (err instanceof Error) {
        return getString(err.name) ?? 'Error';
    }
    if (typeof err === 'string') {
        return 'Error';
    }
    const explicitType = getString(getNested(err, ['type'])) ??
        getString(getNested(err, ['errorType'])) ??
        getString(getNested(err, ['name']));
    if (explicitType)
        return explicitType;
    return 'Unknown';
};
export const getMessageFromErr = (err) => {
    if (typeof err === 'string') {
        return err.trim();
    }
    if (isAxiosLikeError(err)) {
        const candidates = [
            getNested(err, ['response', 'data', 'message']),
            getNested(err, ['response', 'data', 'error']),
            getNested(err, ['response', 'data', 'details']),
            getNested(err, ['response', 'data', 'userMessage']),
            getNested(err, ['response', 'data', 'humanMessage']),
            getNested(err, ['response', 'statusText']),
            err.message,
        ];
        for (const candidate of candidates) {
            const value = getString(candidate);
            if (value)
                return value;
        }
        return 'Request failed';
    }
    if (err instanceof Error) {
        const candidates = [getNested(err, ['cause', 'message']), err.message];
        for (const candidate of candidates) {
            const value = getString(candidate);
            if (value)
                return value;
        }
        return 'An unknown error occurred';
    }
    if (isObject(err)) {
        const candidates = [
            getNested(err, ['message']),
            getNested(err, ['error']),
            getNested(err, ['details']),
            getNested(err, ['reason']),
            getNested(err, ['userMessage']),
            getNested(err, ['humanMessage']),
        ];
        for (const candidate of candidates) {
            const value = getString(candidate);
            if (value)
                return value;
        }
        try {
            return JSON.stringify(err);
        }
        catch {
            return 'An unknown error occurred';
        }
    }
    return 'An unknown error occurred';
};
export const getStackLocation = (err) => {
    const frames = getStackFrames(err);
    if (frames.length === 0) {
        return {
            component: undefined,
            location: 'unknown location',
        };
    }
    const component = getBestComponentFromFrames(frames);
    const location = getBestLocationFromFrames(frames);
    return {
        component,
        location,
    };
};
export const getComponentFromStack = (err) => getStackLocation(err).component;
export const getLocationFromStack = (err) => getStackLocation(err).location;
export const formatFrontendError = (err, options) => {
    const type = getType(err);
    const message = getMessageFromErr(err);
    const route = options?.route ?? getCurrentRoute();
    const { component, location } = getStackLocation(err);
    const typePrefix = type === 'Error'
        ? 'Error'
        : type.includes('Error')
            ? capitalCase(type)
            : `${type} Error`;
    const routePart = route ? `[${route}]` : '';
    const atPart = component
        ? `${component}${routePart} -> ${message}:  ${location}`
        : `${location}: ${message}`;
    return `${typePrefix} at ${atPart}`;
};
//# sourceMappingURL=formatFrontendError.js.map